/**
 * Create tab: the hub for every creation tool, grouped as in the approved design
 * (Graphic/Create.png): AI Video, AI Photo, AI Tools. Home keeps two flagship
 * shortcuts (Image To Video / Edit Image); this page lists everything.
 */
import { icon } from '../components/icon.js';

interface Tool {
  href: string;
  label: string;
  hint?: string;
  img: string;
  badge?: string;
}

const VIDEO: Tool[] = [
  { href: '#/editor/image-to-video', label: 'Image to Video', hint: 'Turn a photo into a stunning video', img: 'image-to-video', badge: 'New' },
  { href: '#/editor/ingredients', label: 'Ingredients – Video', hint: 'Combine multiple images into a video', img: 'ingredients' },
  { href: '#/editor/face_swap_video', label: 'Face Swap – Video', hint: 'Swap a face in your video', img: 'face-swap-video' },
];

const PHOTO: Tool[] = [
  { href: '#/category/change-outfit', label: 'Change Outfit', img: 'change-outfit' },
  { href: '#/editor/face_swap_image', label: 'Face Swap', img: 'face-swap' },
  { href: '#/editor/hair_salon', label: 'Hair Salon', img: 'hair-salon' },
  { href: '#/editor/makeup_styles', label: 'Makeup Styles', img: 'makeup' },
  { href: '#/editor/face_expression', label: 'Face Expression', img: 'face-expression' },
  { href: '#/editor/photo_enhance', label: 'AI Photo Enhance', img: 'photo-enhance', badge: 'HD' },
  { href: '#/editor/photo_enhance?type=restore_colorize', label: 'Restore Old Photo', img: 'restore-colorize', badge: 'New' },
  { href: '#/editor/retouch', label: 'Retouch', img: 'retouch' },
];

const TOOLS: Tool[] = [
  { href: '#/editor/remove_object', label: 'Remove Object', img: 'remove-object' },
  // Reshape Body is hidden for now (owner, 2026-10-24): the model barely changes
  // the body, so results look like the input. Tile image: reshape-body.webp.
  { href: '#/editor/combine', label: 'Combine / Transition', img: 'combine' },
];

function card(t: Tool, cls: string): string {
  return `
    <a class="cr-card ${cls}" href="${t.href}">
      <img src="/img/create/${t.img}.webp" width="360" height="420" alt="" loading="lazy" decoding="async" />
      ${t.badge ? `<span class="cr-badge">${t.badge}</span>` : ''}
      <span class="cr-card-tx">
        <strong>${t.label}</strong>
        ${t.hint ? `<small>${t.hint}</small>` : ''}
      </span>
      ${icon('chevron_right', { cls: 'cr-go' })}
    </a>`;
}

function section(title: string, sub: string, ic: string, tools: Tool[], cls: string, grid: string): string {
  return `
    <section class="cr-section" aria-label="${title}">
      <div class="cr-head">
        <span class="cr-head-ic">${icon(ic)}</span>
        <div><h2>${title}</h2><p>${sub}</p></div>
      </div>
      <div class="cr-grid ${grid}">${tools.map((t) => card(t, cls)).join('')}</div>
    </section>`;
}

export function renderCreate(root: HTMLElement): void {
  root.innerHTML = `
    <div class="create-page">
      <header class="cr-hero">
        <h1 class="cr-title">Create</h1>
        <p>Turn your ideas into stunning photos and videos with AI magic ✨</p>
      </header>
      ${section('AI Video', 'Create amazing videos from your ideas', 'play_arrow', VIDEO, 'cr-tall', 'cr-rail')}
      ${section('AI Photo', 'Edit and transform your photos with AI', 'imagesmode', PHOTO, 'cr-sq', 'cr-g2')}
      ${section('AI Tools', 'More creative tools for your photos and videos', 'auto_awesome', TOOLS, 'cr-wide', 'cr-g2')}
    </div>`;
}
